/**
 * How a click-counting check reports back.
 *
 * The preview lives in its own popup, but an ad's click opens wherever the ad
 * says — a new tab, or the window the popup was opened from. So the page the
 * click lands on and the window showing the preview are usually not the same
 * window, and neither `window.opener` nor the popup's own URL can be relied on
 * to carry the result: ad clicks often set `rel="noopener"`, and a `_top`
 * navigation sets no opener at all.
 *
 * A broadcast needs no relationship between the windows, only the same origin,
 * which holds because the click returns to one of our own routes.
 */

const CHANNEL = 'adn-click-check'

export interface ClickCheckResult {
  /** Where the ad would have sent the user, as the ad server reports it. */
  redirectUrl: string
}

/** Tells every other window of this app that a click came back. */
export function announceClickCheck(result: ClickCheckResult) {
  try {
    const channel = new BroadcastChannel(CHANNEL)
    channel.postMessage(result)
    channel.close()
  } catch {
    // No BroadcastChannel: the landing page still shows the result itself.
  }
}

/** Listens for a click that came back in another window. */
export function onClickCheck(listener: (result: ClickCheckResult) => void): () => void {
  let channel: BroadcastChannel
  try {
    channel = new BroadcastChannel(CHANNEL)
  } catch {
    return () => {}
  }
  const handle = (event: MessageEvent<ClickCheckResult>) => {
    if (event.data && typeof event.data.redirectUrl === 'string') listener(event.data)
  }
  channel.addEventListener('message', handle)
  return () => {
    channel.removeEventListener('message', handle)
    channel.close()
  }
}
